import { Component, OnInit, Inject, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { cloneDeep } from 'lodash';
import { Constants } from '../../../../core/services/constants';
import { DevicesService } from '../../devices.service';
import { HVACModel } from '../../devices.utils';
import { CategoriesService } from '../../categories.service';
import { GeneralService } from '../../../../shared/services/general.service';
import { Device } from '../../devices.model_new';
import { DeviceType, ClimatizationDeviceType, DeviceTypesMap, STATE_VAR, GROUP } from '../../devices.constant';
import { SharedUtils } from '../../../../shared/shared.utils';
import { SubCategory } from '../../categories.model';
import { IotService } from '../../../../core/services/iot.service';

@Component({
  selector: 'app-edit-dialog',
  templateUrl: './edit-dialog.component.html',
  styleUrls: ['./edit-dialog.component.scss']
})
export class EditDialogComponent implements OnInit, OnDestroy {
  /*
   * HVACS VARIABLES
   */
    hvacsMap: Map<string, Map<string, HVACModel>>;
    brandandModels: Map<string, string[]> = new Map<string, string[]>();
    selectedBrand: string;
    selectedModel: string;
  /*
   * CATEGORIES VARIABLES
   */
    categories: any[] = new Array();
    subCategoriesMap: Map<string, string[]> = new Map<string, string[]>();
    selectedCategory: string;
    selectedSubCategory: string;
  /*
   * SHARED VARIABLES
   */
    device: Device;
    name: string;
    locked: boolean;
    DeviceType = DeviceType;
    ClimatizationDeviceType = ClimatizationDeviceType;
    SharedUtils = SharedUtils;
    loading = false;
    properties;

  @Output() action: EventEmitter<string> = new EventEmitter<string>();
  @Output() categoryUpdate: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() hvacUpdate: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() lockedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public dialogRef: MatDialogRef<EditDialogComponent>,
    private devicesService: DevicesService,
    public categoriesService: CategoriesService,
    private generalService: GeneralService,
    private iotService: IotService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    if (data.device !== undefined) {
      this.device = data.device;
      this.name = this.device.getName();
      if (this.device.getUserAttribute('locked')) {
        this.locked = this.device.getUserAttribute('locked') === true;
      }
    }
  }

  ngOnInit(): void {
    this.iotService.connect();
    const deviceType = DeviceTypesMap.map.get(<DeviceType>this.device.getType());
    if (deviceType.subTypes) {
      this.properties = deviceType.subTypes.map.get(this.device.getSubType()).properties;
    } else {
      this.properties = deviceType.properties;
    }
    if (this.device.getType() === DeviceType.CLIMATIZATION) {
      if (this.device.getSubType() === ClimatizationDeviceType.AIR_CONDITIONING) {
        this.loadACBrands();
      }
    } else if (this.device.getType() === DeviceType.CONSUMPTION) {
        this.loadCategories();
    }
  }

  loadACBrands() {
    this.loading = true;
    this.devicesService.getAllHVacsBrands().subscribe(listOfBrands => {
      listOfBrands.acDevices.sort((a, b) => a.brand.localeCompare(b.brand)).forEach(element => {
        this.brandandModels.set(element.brand, element.models);
        if (this.device.getUserAttribute('brand') === element.brand) {
          this.selectedBrand = element.brand;
          this.selectedModel = this.device.getUserAttribute('model');
        }
      });
      this.loading = false;
    });
  }

  getACBrands() {
    return Array.from(this.brandandModels.keys());
  }

  loadCategories() {
    this.loading = true;
    this.categoriesService.getCategories()
      .subscribe(res => {
        res.subscribe(resCategories => {
        this.categories = resCategories;
        resCategories.forEach(cat => {
          this.subCategoriesMap.set(cat.id, cat.subCategories);
          if (this.device.getUserAttribute('category') === cat.id) {
            this.selectedCategory = cat.id;
            this.changeSubCategory(this.device.getUserAttribute('subcategory'));
          }
        });
        this.loading = false;
      })
    });
  }

  getCategoriesIds() {
    return Array.from(this.subCategoriesMap.keys());
  }

  getCategoriesNames() {
    return Array.from(this.categories.keys());
  }

  getSubCategories(id: string) {
    return this.subCategoriesMap.get(id);
  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  changeBrand(value: string) {
    this.selectedBrand = value;
  }

  changeModel(value: string) {
    this.selectedModel = value;
  }

  changeCategory(value: string) {
    this.selectedCategory = value;
  }

  changeSubCategory(value: string) {
    this.selectedSubCategory = value;
  }

  save() {
    this.loading = true;
    let body;
    if (this.device.getType() === DeviceType.CLIMATIZATION) {
      if (this.device.getSubType() === ClimatizationDeviceType.AIR_CONDITIONING) {
        body = {
          moduleinfo: {
            userAttributes: {
              locked: this.locked,
              name: this.name,
              brand: this.selectedBrand,
              model: this.selectedModel,
            }
          }
        };
        this.devicesService.updateModule(this.device.getModuleId(), body).subscribe( res => {
          this.loading = false;
          this.device.setUserAttribute('brand', this.selectedBrand);
          this.device.setUserAttribute('model', this.selectedModel);
          this.device.setUserAttribute('locked', this.locked);
          this.lockedEmitter.emit(this.locked);
          this.action.emit(this.name);
          this.categoryUpdate.emit([this.selectedBrand, this.selectedModel]);
          this.onCloseDialog();
        }, error => {
        });
      } else {
        body = {
          moduleinfo: {
            userAttributes: {
              locked: this.locked,
              name: this.name
            }
          }
        };
        this.devicesService.updateModule(this.device.getModuleId(), body).subscribe( res => {
          this.loading = false;
          this.device.setUserAttribute('locked', this.locked);
          this.lockedEmitter.emit(this.locked);
          this.action.emit(this.name);
          this.onCloseDialog();
        }, error => {
        });
      }
    } else if (this.device.getType() === DeviceType.CONSUMPTION) {
      body = {
        moduleinfo: {
          userAttributes: {
              locked: this.locked,
              name: this.name,
              category: this.selectedCategory,
              subcategory: this.selectedSubCategory
          }
        }
      };
      this.devicesService.updateModule(this.device.getModuleId(), body).subscribe( res => {
        this.loading = false;
        this.action.emit(this.name);
        this.device.setUserAttribute('locked', this.locked);
        this.lockedEmitter.emit(this.locked);
        this.categoryUpdate.emit([this.selectedCategory, this.selectedSubCategory]);
        this.onCloseDialog();
      }, error => {
      });
    } else if (this.device.getType() === DeviceType.GENERATION) {
      body = {
        moduleinfo: {
          userAttributes: {
              name: this.name,
          }
        }
      };
      this.devicesService.updateModule(this.device.getModuleId(), body).subscribe( res => {
        this.loading = false;
        this.action.emit(this.name);
        this.onCloseDialog();
      }, error => {
      });
    }
  }

  loadingStatus() {
    return this.loading;
  }

  getDeviceProperty(property: string): any {
    return this.properties[property];
  }

  ngOnDestroy(): void {
    // this.iotService.disconnect();
  }

  changeLock(event) {
    this.locked = !this.locked;
    // const body = {
    //   moduleinfo: {
    //     userAttributes: {
    //       locked: !this.locked,
    //     }
    //   }
    // };
    // this.devicesService.updateModule(this.device.getModuleId(), body).subscribe( res => {
    //   this.locked = !this.locked;
    //   this.device.setUserAttribute('locked', this.locked);
    //   this.lockedEmitter.emit(this.locked);
    // });
  }

  hasSwitch(): boolean {
    const groups: Set<GROUP> = this.device.getGroups();
    return (groups.has(GROUP.SWITCH)) ;
  }

}



// WEBPACK FOOTER //
// ./src/app/entities/devices/device-detail/edit-dialog/edit-dialog.component.ts